import React from 'react';

import {
  Box,
  Flex,
  Icon,
  MenuItem,
  Skeleton,
  Tag,
  Td,
  Text,
  Tr,
  Tooltip,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FiCopy, FiEdit, FiTrash } from 'react-icons/fi';
import { HiOutlineClock, HiOutlineLightningBolt } from 'react-icons/hi';

import ActionDropdown from 'components/common/ActionDropdown';

import {
  formatDelay,
} from './helpers/complianceHelpers';
import { NotificationIcon } from './NotificationIcon';
import { PolicyComplianceIndicator } from './NotificationPolicy/PolicyComplianceIndicator';
import { NotificationLabel } from './SignalsNotificationLabel';

export const NotificationRow = ({
  index,
  item,
  toggleEditState,
  isLoading,
  deletePreference,
  duplicatePreference,
  arrayHelpers,
  readOnly = false,
  showPolicyColumns = true,
  complianceStatus,
}) => {
  if (isLoading || !item.notification_type?.notification_type) {
    return (
      <Tr>
        <Td colSpan={showPolicyColumns ? 5 : 3}>
          <Skeleton height="20px" width="100%" />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      {/* Type column */}
      <Td maxWidth={{ base: '120px', md: 'none' }}>
        <Flex alignItems="center" gap="2" overflow="hidden">
          <NotificationIcon type={item.notification_type?.notification_type} />
          <Box isTruncated title={item.notification_type?.notification_type}>
            <NotificationLabel notification={item} />
          </Box>
        </Flex>
      </Td>

      {/* Delay column */}
      <Td>
        <Tag variant="roundedOutline" size={{ base: 'sm', md: 'md' }}>
          {item.delay?.value === 'PT0S' ? (
            <Icon as={HiOutlineLightningBolt} color="content.secondary" />
          ) : (
            <Icon as={HiOutlineClock} color="content.secondary" />
          )}
          <Text
            mb="0"
            pl="1"
            textStyle="body2"
            color="content.secondary"
            display={{
              base: item.delay?.value === 'PT0S' ? 'none' : 'block',
              md: 'block',
            }}
          >
            {item.delay?.label}
          </Text>
        </Tag>
      </Td>

      {/* In Policy column - only show if showPolicyColumns is true */}
      {showPolicyColumns && (
        <Td width={{ base: '60px', md: 'auto' }} textAlign="center">
          <PolicyComplianceIndicator
            isCompliant={complianceStatus.isCompliant}
            size="sm"
            showLabel={false}
            userId=""
          />
        </Td>
      )}

      {/* Policy column - only show if showPolicyColumns is true */}
      {showPolicyColumns && (
        <Td display={{ base: 'none', md: 'table-cell' }}>
          {complianceStatus.isCompliant === false ? (
            <Tooltip
              label={complianceStatus.message}
              hasArrow
              placement="top"
              maxW="300px"
            >
              <Text textStyle="body2" color="content.feedback.error.secondary">
                {complianceStatus.requiredDelay ? (
                  <>Required: {formatDelay(complianceStatus.requiredDelay)}</>
                ) : (
                  'Non-compliant'
                )}
              </Text>
            </Tooltip>
          ) : complianceStatus.isCompliant === true && !complianceStatus.complianceFulfilledByGroup ? (
            <Text textStyle="body2" color="content.feedback.success.secondary">
              Compliant
            </Text>
          ) : complianceStatus.isCompliant === true && complianceStatus.complianceFulfilledByGroup ? (
            <Text textStyle="body2" color="content.feedback.success.secondary">
              Not required by policy
            </Text>
          ) : (
            <Text textStyle="body2" color="content.secondary">
              No policy
            </Text>
          )}
        </Td>
      )}

      {/* Actions column */}
      <Td>
        <Flex justifyContent="flex-end">
          {!readOnly && (
            <ActionDropdown inPortal>
              <MenuItem
                onClick={() => toggleEditState(index)}
                data-testid="edit"
              >
                <Flex alignItems="center" gap="2">
                  <Icon as={FiEdit} mr="2" />
                  Edit
                </Flex>
              </MenuItem>
              {item.delay?.value !== 'PT1H' && (
                <MenuItem
                  onClick={() => duplicatePreference(item)}
                  data-testid="duplicate"
                >
                  <Flex alignItems="center" gap="2">
                    <Icon as={FiCopy} mr="2" />
                    Duplicate
                  </Flex>
                </MenuItem>
              )}

              <MenuItem
                onClick={() =>
                  deletePreference(item, index, arrayHelpers.remove)
                }
                data-testid="delete"
              >
                <Flex alignItems="center" gap="2">
                  <Icon as={FiTrash} mr="2" />
                  Delete
                </Flex>
              </MenuItem>
            </ActionDropdown>
          )}
        </Flex>
      </Td>
    </Tr>
  );
};

NotificationRow.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.shape({
    notification_type: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        notification_type: PropTypes.string,
      }),
    ]),
    delay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      }),
    ]),
  }).isRequired,
  toggleEditState: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  formik: PropTypes.object.isRequired,
  deletePreference: PropTypes.func.isRequired,
  duplicatePreference: PropTypes.func.isRequired,
  arrayHelpers: PropTypes.shape({
    remove: PropTypes.func,
  }).isRequired,
  readOnly: PropTypes.bool,
  policyItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      notification_group_method: PropTypes.string,
      max_delay: PropTypes.string,
      priority: PropTypes.string,
    }),
  ),
  notificationPriority: PropTypes.string,
  showPolicyColumns: PropTypes.bool,
};
